import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { formatPrice } from "../utils/helpers";
import { resolveBackendAssetUrl } from "../utils/urls";
import { createDefaultProductBanners, getContrastTextColor, normalizeProductBanners } from "../utils/productBanners";
import { hasConfiguredProductOptions } from "../utils/productOptions";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

const LEGACY_CATEGORY_ROUTES = {
  "/optical": "optical",
  "/sunglasses": "sunglasses",
  "/accessories": "accessories",
  "/childrens": "childrens",
};

const PRODUCT_PAGE_SIZE_OPTIONS = [12, 20, 40, 60, 100];
const DEFAULT_PRODUCT_PAGE_SIZE = 20;

const parsePositiveInt = (value, fallback) => {
  const parsed = parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

// Product Card Component
function ProductCard({ product, canSeePrices, exchangeRates, handleAddToCart, getProductStatus, selectedCurrency, productBanners }) {
  const [isHovered, setIsHovered] = useState(false);
  const status = getProductStatus(product);
  const requiresOptionSelection = hasConfiguredProductOptions(product);
  const isOutOfStock = (product.stock_level ?? 0) <= 0;
  
  const primaryImage = product.images?.find((img) => img.is_primary) || product.images?.[0];
  const alternativeImage = product.images?.length > 1 
    ? product.images.find((img) => !img.is_primary && img.id !== primaryImage?.id) || product.images[1]
    : null;
  
  const displayImage = isHovered && alternativeImage ? alternativeImage : primaryImage;

  return (
    <motion.div 
      key={product.id} 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="product-card group flex h-full flex-col"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/products/${product.id}`} className="block flex-1">
        {/* Image */}
        <div className="bg-gradient-to-r from-black via-gray-900 to-black mb-4 relative">
          {displayImage ? (
            <img 
              src={resolveBackendAssetUrl(displayImage.url)} 
              alt={product.name} 
              className="w-full h-auto transition-all duration-300 group-hover:scale-[1.02]" 
            />
          ) : (
            <div className="flex aspect-[4/3] w-full items-center justify-center text-gray-600">No Image</div>
          )}

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2 transition-opacity duration-500 group-hover:opacity-0">
            {(product.selected_banner_ids || []).map((bannerId) => {
              const banner = productBanners[bannerId];

              if (!banner) {
                return null;
              }

              return (
                <span
                  key={bannerId}
                  className="rounded px-3 py-1 text-xs font-bold"
                  style={{
                    backgroundColor: banner.backgroundColor,
                    color: getContrastTextColor(banner.backgroundColor),
                  }}
                >
                  {banner.name}
                </span>
              );
            })}
            {status && <span className={`bg-${status.color}-600 text-white px-3 py-1 text-xs font-bold rounded`}>{status.label}</span>}
          </div>
        </div>

        {/* Details */}
        <div className="space-y-2 px-4 pt-2 pb-4">
          <h3 className="font-semibold text-lg group-hover:text-gold-500 transition-colors">{product.name}</h3>
          <p className="text-sm text-gray-400 line-clamp-2">{product.description}</p>
          {canSeePrices() ? <p className="text-gold-500 font-bold text-lg">{formatPrice(product.price, selectedCurrency, exchangeRates)}</p> : <p className="text-gray-500 text-sm italic">Login to view prices</p>}
        </div>
      </Link>

      {/* Add to Cart Button */}
      {canSeePrices() && product.status === "active" && (
        <div className="px-4 pb-4 pt-2">
          <button
            onClick={(e) => {
              e.preventDefault();
              handleAddToCart(product);
            }}
            disabled={isOutOfStock}
            className={`w-full ${isOutOfStock ? "bg-gray-800 text-gray-400 border border-gray-700 rounded-lg cursor-not-allowed px-6 py-3" : "btn-primary"}`}
          >
            {isOutOfStock ? "Out of Stock" : requiresOptionSelection ? "Select Options" : "Add to Cart"}
          </button>
        </div>
      )}
    </motion.div>
  );
}

export default function Products() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { canSeePrices } = useAuth();
  const { addToCart } = useCart();
  const { exchangeRates, selectedCurrency } = useCurrency();
  const productsSectionRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productBanners, setProductBanners] = useState(createDefaultProductBanners());
  const [loading, setLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: DEFAULT_PRODUCT_PAGE_SIZE,
    totalPages: 1,
    total: 0,
  });

  // Filters from URL params
  const [filters, setFilters] = useState({
    category: searchParams.get("category") || "",
    search: searchParams.get("search") || "",
    gender: searchParams.get("gender") || "",
    isFeatured: searchParams.get("featured") === "true" || false,
    isNewIn: searchParams.get("new") === "true" || false,
    page: parsePositiveInt(searchParams.get("page"), 1),
    limit: parsePositiveInt(searchParams.get("limit"), DEFAULT_PRODUCT_PAGE_SIZE),
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProductBanners = async () => {
      try {
        const { data } = await api.get("/settings/banners");
        setProductBanners(normalizeProductBanners(data.settings?.product_banners));
      } catch (error) {
        console.error("Error fetching product banners:", error);
      }
    };

    fetchProductBanners();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromSearch = params.get("category") || LEGACY_CATEGORY_ROUTES[location.pathname] || "";
    setFilters((current) => ({
      ...current,
      category: categoryFromSearch,
      search: params.get("search") || "",
      gender: params.get("gender") || "",
      isFeatured: params.get("featured") === "true",
      isNewIn: params.get("new") === "true",
      page: parsePositiveInt(params.get("page"), 1),
      limit: parsePositiveInt(params.get("limit"), DEFAULT_PRODUCT_PAGE_SIZE),
    }));
  }, [location.pathname, location.search]);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      if (filters.category) params.append("category", filters.category);
      if (filters.search) params.append("search", filters.search);
      if (filters.gender) params.append("gender", filters.gender);
      if (filters.isFeatured) params.append("isFeatured", "true");
      if (filters.isNewIn) params.append("isNewIn", "true");
      params.append("page", filters.page);
      params.append("limit", filters.limit);

      const { data } = await api.get(`/products?${params}`);
      setProducts(data.products);
      setPagination(data.pagination);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: key === "page" ? value : 1 };
    setFilters(newFilters);

    // Update URL
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== false && v !== "") {
        const paramName = k === "isFeatured" ? "featured" : k === "isNewIn" ? "new" : k;
        params.set(paramName, v.toString());
      }
    });
    setSearchParams(params);

    if (key === "page" && value !== filters.page) {
      requestAnimationFrame(() => {
        productsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  const clearFilters = () => {
    setFilters({
      category: "",
      search: "",
      gender: "",
      isFeatured: false,
      isNewIn: false,
      page: 1,
      limit: DEFAULT_PRODUCT_PAGE_SIZE,
    });
    setSearchParams({});
  };

  const hasActiveFilters = filters.category || filters.search || filters.gender || filters.isFeatured || filters.isNewIn;

  const handleAddToCart = async (product) => {
    if (hasConfiguredProductOptions(product)) {
      toast.info("Select product options on the product page before adding to cart");
      navigate(`/products/${product.id}`);
      return;
    }

    await addToCart(product, 1);
  };

  const getProductStatus = (product) => {
    if ((product.stock_level ?? 0) <= 0) return { label: "Out of Stock", color: "gray" };
    if (product.status === "discontinued") return { label: "Discontinued", color: "red" };
    if (product.status === "coming_soon") return { label: "Coming Soon", color: "blue" };
    if (product.status === "pre_order") return { label: "Pre-Order", color: "purple" };
    return null;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-black via-gray-900 to-black py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="heading-metallic mb-3 text-3xl sm:mb-4 sm:text-4xl md:text-5xl">Our Collection</h1>
          <p className="text-sm text-gray-400 sm:text-lg">Discover premium eyewear crafted with precision</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4 lg:gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <div className="card lg:sticky lg:top-24">
              <div className="mb-0 flex items-center justify-between gap-2 lg:mb-6 lg:flex-col lg:items-stretch lg:gap-3 xl:flex-row xl:items-center xl:justify-between">
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen((current) => !current)}
                  className="flex w-full items-center justify-between rounded-md border border-gray-700 bg-black/40 px-3 py-2.5 text-left lg:hidden"
                  aria-expanded={mobileFiltersOpen}
                  aria-controls="product-filters-panel"
                >
                  <span className="heading-metallic-soft text-lg">Filters</span>
                  <svg
                    className={`h-5 w-5 text-gray-300 transition-transform duration-200 ${mobileFiltersOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <h2 className="hidden heading-metallic-soft text-xl lg:block">Filters</h2>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="hidden text-sm text-gray-400 hover:text-gold-500 lg:block">
                    Clear All
                  </button>
                )}
              </div>

              {hasActiveFilters && (
                <button onClick={clearFilters} className="mb-3 text-xs text-gray-400 hover:text-gold-500 lg:hidden">
                  Clear All
                </button>
              )}

              <div id="product-filters-panel" className={`${mobileFiltersOpen ? "block" : "hidden"} lg:block`}>
                {/* Search */}
                <div className="mb-4 lg:mb-6">
                  <label className="mb-1.5 block text-sm font-medium">Search</label>
                  <input type="text" value={filters.search} onChange={(e) => updateFilter("search", e.target.value)} placeholder="Product name..." className="w-full rounded border border-gray-700 bg-gradient-to-r from-black via-gray-900 to-black px-3 py-2 text-sm focus:border-gold-500 focus:outline-none" />
                </div>

                {/* Categories */}
                <div className="mb-4 lg:mb-6">
                  <label className="mb-1.5 block text-sm font-medium">Category</label>
                  <select value={filters.category} onChange={(e) => updateFilter("category", e.target.value)} className="w-full rounded border border-gray-700 bg-gradient-to-r from-black via-gray-900 to-black px-3 py-2 text-sm focus:border-gold-500 focus:outline-none">
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.slug}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Gender */}
                <div className="mb-4 lg:mb-6">
                  <label className="mb-1.5 block text-sm font-medium">Gender</label>
                  <select value={filters.gender} onChange={(e) => updateFilter("gender", e.target.value)} className="w-full rounded border border-gray-700 bg-gradient-to-r from-black via-gray-900 to-black px-3 py-2 text-sm focus:border-gold-500 focus:outline-none">
                    <option value="">All</option>
                    <option value="men">Men</option>
                    <option value="women">Women</option>
                    <option value="unisex">Unisex</option>
                  </select>
                </div>

                {/* Quick Filters */}
                <div className="space-y-2.5">
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" checked={filters.isFeatured} onChange={(e) => updateFilter("isFeatured", e.target.checked)} className="mr-2" />
                    <span className="text-sm leading-none">Featured Only</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" checked={filters.isNewIn} onChange={(e) => updateFilter("isNewIn", e.target.checked)} className="mr-2" />
                    <span className="text-sm leading-none">New Arrivals</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div ref={productsSectionRef} className="lg:col-span-3 scroll-mt-24">
            {/* Results Header */}
            <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-gray-400">{loading ? "Loading..." : `${pagination.total} products found`}</p>
              <label className="flex items-center gap-2 text-sm text-gray-400">
                <span>Show</span>
                <select
                  value={filters.limit}
                  onChange={(e) => updateFilter("limit", parsePositiveInt(e.target.value, DEFAULT_PRODUCT_PAGE_SIZE))}
                  className="rounded border border-gray-700 bg-black px-3 py-2 text-white focus:border-gold-500 focus:outline-none"
                >
                  {PRODUCT_PAGE_SIZE_OPTIONS.map((limit) => (
                    <option key={limit} value={limit}>
                      {limit}
                    </option>
                  ))}
                </select>
                <span>per page</span>
              </label>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="card animate-pulse">
                    <div className="aspect-square bg-gray-800 mb-4"></div>
                    <div className="h-4 bg-gray-800 rounded mb-2"></div>
                    <div className="h-4 bg-gray-800 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400 text-lg mb-4">No products found</p>
                <button onClick={clearFilters} className="btn-primary">
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                {/* Products Grid */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
                  {products.map((product) => (
                    <ProductCard 
                      key={product.id}
                      product={product}
                      canSeePrices={canSeePrices}
                      exchangeRates={exchangeRates}
                      handleAddToCart={handleAddToCart}
                      getProductStatus={getProductStatus}
                      selectedCurrency={selectedCurrency}
                      productBanners={productBanners}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="mt-10 flex flex-wrap justify-center gap-2 sm:mt-12">
                    <button onClick={() => updateFilter("page", filters.page - 1)} disabled={filters.page === 1} className="btn-secondary px-4 py-2 disabled:opacity-50">
                      Previous
                    </button>

                    <div className="flex flex-wrap justify-center gap-1">
                      {[...Array(pagination.totalPages)].map((_, i) => (
                        <button
                          key={i + 1}
                          onClick={() => updateFilter("page", i + 1)}
                          aria-current={filters.page === i + 1 ? "page" : undefined}
                          className={`px-4 py-2 font-bold transition-colors ${
                            filters.page === i + 1
                              ? "rounded-lg border-2 border-gold-300 bg-gold-500 text-black shadow-lg shadow-gold-500/30"
                              : "btn-secondary opacity-85"
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>

                    <button onClick={() => updateFilter("page", filters.page + 1)} disabled={filters.page === pagination.totalPages} className="btn-secondary px-4 py-2 disabled:opacity-50">
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
